import type { ReactNode } from 'react';

import {
  JupiterFetchRoutesError,
  JupiterRoutePlanDoesNotConsumeAllTheAmountError,
} from '@/lib/errors/jupiter';

import { FeeOnTransferAlert } from '@/components/TransactionModule/Alerts/FeeOnTransferAlert';
import { InlineAlertBox } from '@/components/TransactionModule/Alerts/InlineAlertBox';
import { PriceImpactAlert } from '@/components/TransactionModule/Alerts/PriceImpactAlert';
import { SwapTransactionAlert } from '@/components/TransactionModule/Alerts/SwapTransaction';
import { hasFeeOnTransfer } from '@/components/TransactionModule/MarketMode/utils';

import { NATIVE_TOKEN_DATA_ADDRESSES } from '@/constants/addresses';
import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';
import {
  NATIVE_TOKEN_SYMBOL_PER_CHAIN,
  NETWORK_NAME_PER_CHAIN_ID,
  WRAPPED_NATIVE_TOKEN_SYMBOL_PER_CHAIN,
} from '@/constants/chain';

import { useTokenResults } from '@/hooks/tokenQueries/useTokenResults';
import { useCheckoutMode } from '@/hooks/useCheckoutMode';

import { linkStyles } from '@/styles/links';

import { isTokenAddressNativeAsset } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { routes } from '@/utils/routes';

import { useModuleContext } from '../ModuleContext';
import type { TradeQuote } from '../tradeQuote';
import type { ValidationResult } from '../useValidation';
import { getPrimaryValidationResult } from '../validation/common';

const alertActionStyles = linkStyles({
  font: 'LABEL_MD',
  class: ['text-token-red-1300', 'underline'],
});

const infoAlertActionStyles = linkStyles({
  font: 'LABEL_MD',
  class: ['text-token-gray-1200', 'underline'],
});

/** Inputs for typed checkout alert selection and token-specific actions. */
type CheckoutAlertsProps = {
  results: readonly ValidationResult[];
  quote: TradeQuote;
};

/** Maps the primary typed validation result and additive transfer-fee warning to legacy alerts. */
export function CheckoutAlerts({ results, quote }: CheckoutAlertsProps) {
  const { onSelectToken, sellToken, buyToken } = useModuleContext();
  const chainId = sellToken?.chainId ?? buyToken?.chainId;
  const [checkoutMode, setCheckoutMode] = useCheckoutMode();
  const primary = getPrimaryValidationResult(results);
  const alternativeAddress = chainId == null ? undefined : NATIVE_TOKEN_DATA_ADDRESSES[chainId];
  const { tokenMap } = useTokenResults(alternativeAddress ? [alternativeAddress] : [], chainId);
  const alternativeToken =
    chainId != null && alternativeAddress ? tokenMap?.[chainId]?.[alternativeAddress] : undefined;

  const switchToAlternativeToken = () => {
    if (!alternativeToken) return;
    const direction = isTokenAddressNativeAsset(buyToken?.address, chainId) ? 'buy' : 'sell';
    onSelectToken(alternativeToken, direction);
  };

  let primaryAlert: ReactNode = null;

  switch (primary?.code) {
    case 'native-token-unsupported':
      if (chainId != null) {
        primaryAlert = (
          <InlineAlertBox
            level="info"
            title="Native tokens unsupported"
            description={
              <div>
                Native tokens are not supported in limit orders.
                <br />
                <button
                  type="button"
                  className={infoAlertActionStyles}
                  onClick={switchToAlternativeToken}
                >
                  Switch from {NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId]} to{' '}
                  {alternativeToken?.symbol ?? WRAPPED_NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId] ?? ''}
                </button>{' '}
                to continue.
              </div>
            }
          />
        );
      }
      break;
    case 'superstate-token-unsupported':
      primaryAlert = (
        <InlineAlertBox
          level="info"
          title="Superstate tokens unsupported"
          description="Superstate tokens are not supported in limit orders"
        />
      );
      break;
    case 'wrap-unwrap-unsupported':
      primaryAlert = (
        <InlineAlertBox
          level="info"
          title="Token wrap/unwrap unsupported"
          description={
            <div>
              Token wrap/unwrap is not supported in limit orders.
              <br />
              <button
                type="button"
                className={infoAlertActionStyles}
                onClick={() => setCheckoutMode('market')}
              >
                Switch to market mode
              </button>{' '}
              to continue.
            </div>
          }
        />
      );
      break;
    case 'high-price-impact':
      primaryAlert = (
        <PriceImpactAlert
          priceImpact={quote.priceImpact ?? { percent: primary.priceImpactPercent, usd: 0 }}
        />
      );
      break;
    case 'advanced-order-type-unsupported':
      primaryAlert = (
        <InlineAlertBox
          level="info"
          title={`${ADVANCED_ORDER_TYPE_LABELS[primary.orderType]} is not available on ${NETWORK_NAME_PER_CHAIN_ID[primary.chainId]} yet.`}
          description="Switch to a supported network to continue."
        />
      );
      break;
    case 'smart-wallet-unsupported':
      primaryAlert = (
        <InlineAlertBox
          level="info"
          title="Limit orders are not yet available for smart wallets."
          description="Use another wallet app or extension."
        />
      );
      break;
    case 'token-decimals-unavailable':
      primaryAlert = (
        <InlineAlertBox
          title="Token decimals unavailable"
          description={
            <div>
              This token is missing decimal information.
              <br />
              Please{' '}
              <a href={routes.HELP} target="_blank" className={alertActionStyles}>
                contact support
              </a>
              .
            </div>
          }
        />
      );
      break;
    case 'high-fees':
      primaryAlert = <SwapTransactionAlert alertType="feesTooHigh" />;
      break;
    case 'sell-amount-too-small':
      primaryAlert = (
        <InlineAlertBox
          title="Sell Amount Too Small"
          description={`There is not enough ${sellToken?.symbol} to cover the gas fees. Please enter a larger sell amount to continue.`}
        />
      );
      break;
    case 'rwa-geo-blocked': {
      const isBuyTokenBlocked = Boolean(
        buyToken &&
          primary.blockedTokenAddresses.some(
            (address) =>
              normalizeTokenAddress(address, buyToken.chainId) ===
              normalizeTokenAddress(buyToken.address, buyToken.chainId),
          ),
      );
      const isSellTokenBlocked = Boolean(
        sellToken &&
          primary.blockedTokenAddresses.some(
            (address) =>
              normalizeTokenAddress(address, sellToken.chainId) ===
              normalizeTokenAddress(sellToken.address, sellToken.chainId),
          ),
      );
      const tokenSymbol = isBuyTokenBlocked
        ? buyToken?.symbol
        : isSellTokenBlocked
          ? sellToken?.symbol
          : undefined;
      primaryAlert = <SwapTransactionAlert alertType="rwaGeoBlocked" tokenSymbol={tokenSymbol} />;
      break;
    }
    case 'buy-token-blocked':
      primaryAlert = (
        <SwapTransactionAlert alertType="unsupportedToken" tokenSymbol={buyToken?.symbol} />
      );
      break;
    case 'sell-token-blocked':
      primaryAlert = (
        <SwapTransactionAlert alertType="unsupportedToken" tokenSymbol={sellToken?.symbol} />
      );
      break;
    case 'cross-chain-unsupported':
      primaryAlert = (
        <InlineAlertBox
          level="info"
          title="Tokens are not on the same network"
          description={
            <div>
              For trading across networks use{' '}
              <button
                type="button"
                className={infoAlertActionStyles}
                onClick={() => setCheckoutMode('cross-chain')}
              >
                Cross Chain
              </button>{' '}
              transaction type.
            </div>
          }
        />
      );
      break;
    case 'quote-failed':
      if (primary.error instanceof JupiterFetchRoutesError) {
        primaryAlert = (
          <InlineAlertBox
            title="No routes found"
            description="There are no routes available for the selected tokens. Please try a different token pair or try again later."
          />
        );
      } else if (primary.error instanceof JupiterRoutePlanDoesNotConsumeAllTheAmountError) {
        primaryAlert = (
          <InlineAlertBox
            title="Not enough liquidity"
            description="There is not enough liquidity to complete the swap. Please try a different token pair or try again later."
          />
        );
      }
      break;
    default:
      break;
  }

  const tokenMetadata =
    (quote.source === 'swap' || quote.source === 'intents') && quote.raw
      ? quote.raw.tokenMetadata
      : undefined;
  const feeOnTransferAlert =
    checkoutMode === 'market' && buyToken && sellToken && hasFeeOnTransfer(tokenMetadata) ? (
      <FeeOnTransferAlert
        buyTokenSymbol={buyToken.symbol}
        sellTokenSymbol={sellToken.symbol}
        tokenMetadata={tokenMetadata}
      />
    ) : null;

  if (!primaryAlert && !feeOnTransferAlert) return null;

  return (
    <div className="px-6 mt-6">
      {primaryAlert}
      {feeOnTransferAlert}
    </div>
  );
}
